import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { registerWorkbenchContribution2 } from 'vscode/vscode/vs/workbench/common/contributions';
import { IBulkEditService } from 'vscode/vscode/vs/editor/browser/services/bulkEditService';
import { BulkEditPane } from './bulkEditPane.js';
import { Extensions } from 'vscode/vscode/vs/workbench/common/views';
import { IViewsService } from 'vscode/vscode/vs/workbench/services/views/common/viewsService.service';
import { FocusedViewContext } from 'vscode/vscode/vs/workbench/common/contextkeys';
import { localizeWithPath, localize2WithPath } from 'vscode/vscode/vs/nls';
import { ViewPaneContainer } from 'vscode/vscode/vs/workbench/browser/parts/views/viewPaneContainer';
import { RawContextKey, ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { IEditorGroupsService } from 'vscode/vscode/vs/workbench/services/editor/common/editorGroupsService.service';
import { WorkbenchListFocusContextKey } from 'vscode/vscode/vs/platform/list/browser/listService';
import { SyncDescriptor } from 'vscode/vscode/vs/platform/instantiation/common/descriptors';
import { registerAction2, Action2, MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { EditorResourceAccessor, SideBySideEditor } from 'vscode/vscode/vs/workbench/common/editor';
import { CancellationTokenSource } from 'vscode/vscode/vs/base/common/cancellation';
import { IDialogService } from 'vscode/vscode/vs/platform/dialogs/common/dialogs.service';
import Severity$1 from 'vscode/vscode/vs/base/common/severity';
import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { registerIcon } from 'vscode/vscode/vs/platform/theme/common/iconRegistry';
import { IPaneCompositePartService } from 'vscode/vscode/vs/workbench/services/panecomposite/browser/panecomposite.service';

var BulkEditPreviewContribution_1;
const _moduleId = "vs/workbench/contrib/bulkEdit/browser/preview/bulkEdit.contribution";
async function getBulkEditPane(viewsService) {
    const view = await viewsService.openView(BulkEditPane.ID, true);
    if (view instanceof BulkEditPane) {
        return view;
    }
    return undefined;
}
let UXState = class UXState {
    constructor(_paneCompositeService, _editorGroupsService) {
        this._paneCompositeService = _paneCompositeService;
        this._editorGroupsService = _editorGroupsService;
        this._activePanel = _paneCompositeService.getActivePaneComposite(1 )?.getId();
    }
    async restore(panels, editors) {
        if (panels) {
            if (typeof this._activePanel === 'string') {
                await this._paneCompositeService.openPaneComposite(this._activePanel, 1 );
            }
            else {
                this._paneCompositeService.hideActivePaneComposite(1 );
            }
        }
        if (editors) {
            for (const group of this._editorGroupsService.groups) {
                const previewEditors = [];
                for (const input of group.editors) {
                    const resource = EditorResourceAccessor.getCanonicalUri(input, { supportSideBySide: SideBySideEditor.PRIMARY });
                    if (resource?.scheme === BulkEditPane.Schema) {
                        previewEditors.push(input);
                    }
                }
                if (previewEditors.length) {
                    group.closeEditors(previewEditors, { preserveFocus: true });
                }
            }
        }
    }
};
UXState = ( (__decorate([
    ( (__param(0, IPaneCompositePartService))),
    ( (__param(1, IEditorGroupsService)))
], UXState)));
class PreviewSession {
    constructor(uxState, cts = ( (new CancellationTokenSource()))) {
        this.uxState = uxState;
        this.cts = cts;
    }
}
let BulkEditPreviewContribution = class BulkEditPreviewContribution {
    static { BulkEditPreviewContribution_1 = this; }
    static { this.ID = 'workbench.contrib.bulkEditPreview'; }
    static { this.ctxEnabled = ( (new RawContextKey('refactorPreview.enabled', false))); }
    constructor(_paneCompositeService, _viewsService, _editorGroupsService, _dialogService, bulkEditService, contextKeyService) {
        this._paneCompositeService = _paneCompositeService;
        this._viewsService = _viewsService;
        this._editorGroupsService = _editorGroupsService;
        this._dialogService = _dialogService;
        bulkEditService.setPreviewHandler(edits => this._previewEdit(edits));
        this._ctxEnabled = BulkEditPreviewContribution_1.ctxEnabled.bindTo(contextKeyService);
    }
    async _previewEdit(edits) {
        this._ctxEnabled.set(true);
        const uxState = this._activeSession?.uxState ?? ( (new UXState(this._paneCompositeService, this._editorGroupsService)));
        const view = await getBulkEditPane(this._viewsService);
        if (!view) {
            this._ctxEnabled.set(false);
            return edits;
        }
        if (view.hasInput()) {
            const { confirmed } = await this._dialogService.confirm({
                type: Severity$1.Info,
                message: ( localizeWithPath(_moduleId, 0, "Another refactoring is being previewed.")),
                detail: ( localizeWithPath(
                    _moduleId,
                    1,
                    "Press 'Continue' to discard the previous refactoring and continue with the current refactoring."
                )),
                primaryButton: ( localizeWithPath(_moduleId, 2, "&&Continue"))
            });
            if (!confirmed) {
                return [];
            }
        }
        let session;
        if (this._activeSession) {
            await this._activeSession.uxState.restore(false, true);
            this._activeSession.cts.dispose(true);
            session = ( (new PreviewSession(uxState)));
        }
        else {
            session = ( (new PreviewSession(uxState)));
        }
        this._activeSession = session;
        try {
            return (await view.setInput(edits, session.cts.token)) ?? [];
        }
        finally {
            if (this._activeSession === session) {
                await this._activeSession.uxState.restore(true, true);
                this._activeSession.cts.dispose();
                this._ctxEnabled.set(false);
                this._activeSession = undefined;
            }
        }
    }
};
BulkEditPreviewContribution = BulkEditPreviewContribution_1 = ( (__decorate([
    ( (__param(0, IPaneCompositePartService))),
    ( (__param(1, IViewsService))),
    ( (__param(2, IEditorGroupsService))),
    ( (__param(3, IDialogService))),
    ( (__param(4, IBulkEditService))),
    ( (__param(5, IContextKeyService)))
], BulkEditPreviewContribution)));
registerAction2(class ApplyAction extends Action2 {
    constructor() {
        super({
            id: 'refactorPreview.apply',
            title: ( localize2WithPath(_moduleId, 3, "Apply Refactoring")),
            category: ( localize2WithPath(_moduleId, 4, "Refactor Preview")),
            icon: Codicon.check,
            precondition: ( (ContextKeyExpr.and(BulkEditPreviewContribution.ctxEnabled, BulkEditPane.ctxHasCheckedChanges))),
            menu: [{
                    id: MenuId.BulkEditContext,
                    order: 1
                }],
            keybinding: {
                weight: 100  - 10,
                when: ( (ContextKeyExpr.and(
                    BulkEditPreviewContribution.ctxEnabled,
                     (FocusedViewContext.isEqualTo(BulkEditPane.ID))
                ))),
                primary: 2048  + 3 ,
            }
        });
    }
    async run(accessor) {
        const viewsService = accessor.get(IViewsService);
        const view = await getBulkEditPane(viewsService);
        view?.accept();
    }
});
registerAction2(class DiscardAction extends Action2 {
    constructor() {
        super({
            id: 'refactorPreview.discard',
            title: ( localize2WithPath(_moduleId, 5, "Discard Refactoring")),
            category: ( localize2WithPath(_moduleId, 4, "Refactor Preview")),
            icon: Codicon.clearAll,
            precondition: BulkEditPreviewContribution.ctxEnabled,
            menu: [{
                    id: MenuId.BulkEditContext,
                    order: 2
                }]
        });
    }
    async run(accessor) {
        const viewsService = accessor.get(IViewsService);
        const view = await getBulkEditPane(viewsService);
        view?.discard();
    }
});
registerAction2(class ToggleAction extends Action2 {
    constructor() {
        super({
            id: 'refactorPreview.toggleCheckedState',
            title: ( localize2WithPath(_moduleId, 6, "Toggle Change")),
            category: ( localize2WithPath(_moduleId, 4, "Refactor Preview")),
            precondition: BulkEditPreviewContribution.ctxEnabled,
            keybinding: {
                weight: 200 ,
                when: WorkbenchListFocusContextKey,
                primary: 10 ,
            },
            menu: {
                id: MenuId.BulkEditContext,
                group: 'navigation'
            }
        });
    }
    async run(accessor) {
        const viewsService = accessor.get(IViewsService);
        const view = await getBulkEditPane(viewsService);
        view?.toggleChecked();
    }
});
registerAction2(class GroupByFile extends Action2 {
    constructor() {
        super({
            id: 'refactorPreview.groupByFile',
            title: ( localize2WithPath(_moduleId, 7, "Group Changes By File")),
            category: ( localize2WithPath(_moduleId, 4, "Refactor Preview")),
            icon: Codicon.ungroupByRefType,
            precondition: ( (ContextKeyExpr.and(
                BulkEditPane.ctxHasCategories,
                 (BulkEditPane.ctxGroupByFile.negate()),
                BulkEditPreviewContribution.ctxEnabled
            ))),
            menu: [{
                    id: MenuId.BulkEditTitle,
                    when: ( (ContextKeyExpr.and(
                        BulkEditPane.ctxHasCategories,
                         (BulkEditPane.ctxGroupByFile.negate())
                    ))),
                    group: 'navigation',
                    order: 3,
                }]
        });
    }
    async run(accessor) {
        const viewsService = accessor.get(IViewsService);
        const view = await getBulkEditPane(viewsService);
        view?.groupByFile();
    }
});
registerAction2(class GroupByType extends Action2 {
    constructor() {
        super({
            id: 'refactorPreview.groupByType',
            title: ( localize2WithPath(_moduleId, 8, "Group Changes By Type")),
            category: ( localize2WithPath(_moduleId, 4, "Refactor Preview")),
            icon: Codicon.groupByRefType,
            precondition: ( (ContextKeyExpr.and(
                BulkEditPane.ctxHasCategories,
                BulkEditPane.ctxGroupByFile,
                BulkEditPreviewContribution.ctxEnabled
            ))),
            menu: [{
                    id: MenuId.BulkEditTitle,
                    when: ( (ContextKeyExpr.and(BulkEditPane.ctxHasCategories, BulkEditPane.ctxGroupByFile))),
                    group: 'navigation',
                    order: 3
                }]
        });
    }
    async run(accessor) {
        const viewsService = accessor.get(IViewsService);
        const view = await getBulkEditPane(viewsService);
        view?.groupByType();
    }
});
registerAction2(class ToggleGrouping extends Action2 {
    constructor() {
        super({
            id: 'refactorPreview.toggleGrouping',
            title: ( localize2WithPath(_moduleId, 8, "Group Changes By Type")),
            category: ( localize2WithPath(_moduleId, 4, "Refactor Preview")),
            icon: Codicon.listTree,
            toggled: ( (BulkEditPane.ctxGroupByFile.negate())),
            precondition: ( (ContextKeyExpr.and(BulkEditPane.ctxHasCategories, BulkEditPreviewContribution.ctxEnabled))),
            menu: [{
                    id: MenuId.BulkEditContext,
                    order: 3
                }]
        });
    }
    async run(accessor) {
        const viewsService = accessor.get(IViewsService);
        const view = await getBulkEditPane(viewsService);
        view?.toggleGrouping();
    }
});
registerWorkbenchContribution2(BulkEditPreviewContribution.ID, BulkEditPreviewContribution, 2 );
const refactorPreviewViewIcon = registerIcon('refactor-preview-view-icon', Codicon.lightbulb, ( localizeWithPath(_moduleId, 9, 'View icon of the refactor preview view.')));
const container = ( (Registry.as(Extensions.ViewContainersRegistry))).registerViewContainer({
    id: BulkEditPane.ID,
    title: ( localize2WithPath(_moduleId, 10, "Refactor Preview")),
    hideIfEmpty: true,
    ctorDescriptor: ( (new SyncDescriptor(
        ViewPaneContainer,
        [BulkEditPane.ID, { mergeViewWithContainerWhenSingleView: true }]
    ))),
    icon: refactorPreviewViewIcon,
    storageId: BulkEditPane.ID
}, 1 );
( (Registry.as(Extensions.ViewsRegistry))).registerViews([{
        id: BulkEditPane.ID,
        name: ( localize2WithPath(_moduleId, 10, "Refactor Preview")),
        when: BulkEditPreviewContribution.ctxEnabled,
        ctorDescriptor: ( (new SyncDescriptor(BulkEditPane))),
        containerIcon: refactorPreviewViewIcon,
    }], container);
